import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, Observable, of, switchMap, take } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  AddressResponse,
  Assets,
  CustomerServiceProfile,
  GenericStatusCodeResponse,
  PhoneNumber,
} from '@digital-platform/rs-addresses/domain';
import { AnalyticsService as RsxAnalyticsW3cService } from '@digital-platform/rs-analytics/services';
import { AddressRaw } from '@digital-platform/rs-cms/domain';
import { GenericResponse } from '@digital-platform/rs-shared/domain';

@Injectable({
  providedIn: 'root',
})
export class AddressesService {
  private API_BASE_URL = '/api/v1';
  private publicPickupUrl = `${this.API_BASE_URL}/publicPickup`;
  private profilesUrl = `${this.API_BASE_URL}/customerServiceProfiles`;

  constructor(
    private httpClient: HttpClient,
    private rsxAnalyticsW3cService: RsxAnalyticsW3cService
  ) {}

  public searchForAddresses(
    term: string,
    httpSearchParams: HttpParams,
    currentSearchUrl: string,
    primarySearchKey: string
  ): Observable<AddressRaw[]> {
    return this.httpClient
      .get<AddressResponse>(currentSearchUrl, {
        params: httpSearchParams.set(primarySearchKey, term),
      })
      .pipe(
        take(1),
        map((response: AddressResponse) => {
          if (Array.isArray(response)) {
            return response.data;
          } else {
            // If the response is from /addresses, put it into an array here for easy usage in the calling methods, since /auto is already returned as an array
            return [...response.data];
          }
        })
      );
  }

  public checkIfAddressIsExistingUser(
    serviceAddress: AddressRaw
  ): Observable<Assets | undefined> {
    const params = new HttpParams().set(
      'siteAddressHash',
      serviceAddress.id || serviceAddress.addressHash || ''
    );

    return this.httpClient
      .get<GenericStatusCodeResponse<Assets>>(`${this.publicPickupUrl}`, {
        params,
      })
      .pipe(map((response) => response.data));
  }

  public getCustomerServicePhoneNumber(
    latitude: number,
    longitude: number
  ): Observable<PhoneNumber> {
    const httpParams = new HttpParams()
      .append('latitude', `${latitude}`)
      .append('longitude', `${longitude}`);

    return this.httpClient
      .get<GenericStatusCodeResponse<CustomerServiceProfile[]>>(
        this.profilesUrl,
        { params: httpParams }
      )
      .pipe(
        map((response) => response.data),
        switchMap((customerServiceProfile) =>
          of(
            this.getFirstPrimaryPhoneNumberOfFirstContact(
              customerServiceProfile
            )
          )
        ),
        catchError(() => of())
      );
  }

  public getProfiles(
    latitude: number,
    longitude: number
  ): Observable<CustomerServiceProfile[]> {
    const httpParams = new HttpParams()
      .append('latitude', `${latitude}`)
      .append('longitude', `${longitude}`);

    return this.httpClient
      .get<GenericResponse<CustomerServiceProfile[]>>(this.profilesUrl, {
        params: httpParams,
      })
      .pipe(
        take<GenericResponse<CustomerServiceProfile[]>>(1),
        map((profiles: GenericResponse<CustomerServiceProfile[]>) =>
          this.checkBlankServiceNumber(profiles.data)
        )
      );
  }

  public checkBlankServiceNumber(data: CustomerServiceProfile[]) {
    if (data.length === 0) {
      const blankNumberCode = 'Blank_Customer_Service_Number';

      this.rsxAnalyticsW3cService.registerBlankServiceNumber(blankNumberCode);
    }

    return data;
  }

  public getPrimaryCustomerServicePhoneNumber(
    latitude: number,
    longitude: number
  ): Observable<PhoneNumber> {
    return this.getProfiles(latitude, longitude).pipe(
      catchError(() => of([] as CustomerServiceProfile[])),
      switchMap((res: CustomerServiceProfile[]) =>
        of(this.getFirstPrimaryPhoneNumberOfFirstContact(res))
      )
    );
  }

  public getFirstPrimaryPhoneNumberOfFirstContact(
    contacts: CustomerServiceProfile[] = []
  ): PhoneNumber {
    const firstContactInfo = this.getFirstContactInfo(contacts);

    return this.getFirstPrimaryPhoneNumber(firstContactInfo);
  }

  private getFirstContactInfo(
    contacts: CustomerServiceProfile[]
  ): CustomerServiceProfile {
    const firstContactInfo = (Array.isArray(contacts) ? contacts : [])[0];

    return firstContactInfo;
  }

  private getFirstPrimaryPhoneNumber(
    contactInfo: CustomerServiceProfile
  ): PhoneNumber {
    // Check for customer service phone override. If one exists, return it rather than the number
    // from the contactInfo object.
    const phoneNumbers = contactInfo?.phoneNumbers;
    const firstPrimaryPhoneNumber = phoneNumbers?.filter(
      (phone: PhoneNumber) => phone.primary
    )?.[0];

    return firstPrimaryPhoneNumber;
  }
}
